import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { useCartContext } from '../api/cart-provider.js';
import { addCartLine, type ReleasedLine } from '../api/use-cart.js';
import { useServices } from '../api/use-catalogue.js';
import { loadDismissed, rememberDismissed } from './dismissed-rows.js';
import { useSlot } from '../api/use-slot.js';
import { when } from './when.js';
import { BusyLabel } from './busy-label.js';

/**
 * The hold ran out, and these are the hours it let go of.
 *
 * A BANNER, NOT A SCREEN. Closing is not a screen and publishing is not a screen;
 * neither is this. It reads from the cart, so it appears wherever the parent
 * happens to be rather than only where they were when the clock ran out — which
 * is the whole point, because the parent this exists for is the one who walked
 * away.
 *
 * Warn-toned, because design-principles §7 reserves warn for cancellation and for
 * taking something off sale, and a lapsed hold is exactly that kind of event.
 *
 * NO COMPOSED SUMMARY. A header of fact — "Your cart has expired" — then one row per
 * released hour, each carrying its time, its lesson product, and the athletes'
 * names one per line. §4: the list is the truth.
 */
export function ReleasedBanner() {
  const { state, reload, setReleasedVisible } = useCartContext();
  const nothingReleased = state.status !== 'ready' || state.released.length === 0;

  // Reported from here as well as from ReleasedRows, because "there is nothing
  // to show" never reaches ReleasedRows at all — and the cart screen underneath
  // has to know that just as much as it has to know the opposite.
  useEffect(() => {
    if (nothingReleased) setReleasedVisible(false);
  }, [nothingReleased, setReleasedVisible]);

  // The hooks in ReleasedRows belong there rather than here, so that the
  // ordinary case — nothing released, which is almost always — costs no
  // catalogue request and no slot checks at all.
  if (nothingReleased) return null;

  return <ReleasedRows lines={state.released} reloadCart={reload} />;
}

/** One released hour, however many children were in it. */
interface Group {
  key: string;
  publishedSlotId: string;
  serviceId: string;
  capacityTierId: string;
  slotStart: string;
  athletes: { id: string; name: string }[];
}

type RowStatus = 'checking' | 'open' | 'taken';

function ReleasedRows({
  lines,
  reloadCart,
}: {
  lines: ReleasedLine[];
  reloadCart: () => void;
}) {
  const services = useServices();
  const { setReleasedVisible } = useCartContext();

  // Seeded from storage, so a row waved away an hour ago does not come back
  // every time the page is loaded. Ruled 30 September.
  const [dismissed, setDismissed] = useState<string[]>(loadDismissed);
  const [status, setStatus] = useState<Record<string, RowStatus>>({});
  const [acted, setActed] = useState(false);

  // ONE ROW PER HOUR, not per child. Two children released from the same hour is
  // one thing that happened, and the names go on it one per line — §4 again: who
  // is affected is a list, never "2 athletes".
  const groups = useMemo(() => byHour(lines), [lines]);

  const productNames = useMemo(() => {
    const byTier = new Map<string, string>();
    if (services.status === 'ready') {
      for (const service of services.value) {
        for (const product of service.products) byTier.set(product.tier_id, product.name);
      }
    }
    return byTier;
  }, [services]);

  const report = useCallback((key: string, next: RowStatus) => {
    setStatus((current) => (current[key] === next ? current : { ...current, [key]: next }));
  }, []);

  const drop = useCallback((key: string) => {
    rememberDismissed(key);
    setDismissed((current) => (current.includes(key) ? current : [...current, key]));
    setActed(true);
  }, []);

  const added = useCallback(
    (key: string) => {
      rememberDismissed(key);
      setDismissed((current) => (current.includes(key) ? current : [...current, key]));
      setActed(true);
      // The hour is held again, so the clock has to come back on the primary
      // button — which means the cart, not just this banner, has to be re-read.
      reloadCart();
    },
    [reloadCart],
  );

  const visible = groups.filter((group) => !dismissed.includes(group.key));
  const openCount = visible.filter((group) => status[group.key] === 'open').length;
  const showing = visible.length > 0 && !(openCount === 0 && acted);

  // Derived from the same two conditions the render uses, so the cart screen and
  // this banner cannot disagree about whether a banner is on screen.
  useEffect(() => {
    setReleasedVisible(showing);
    return () => setReleasedVisible(false);
  }, [showing, setReleasedVisible]);

  if (visible.length === 0) return null;

  // THE BANNER'S LIFE IS GOVERNED BY THE ROWS THAT CAN BE ACTED ON. Deal with the
  // last hour that was still available — by adding it or by cancelling it — and
  // the whole banner goes, Taken rows with it: there is nothing left to tidy up
  // that a parent could do anything about.
  //
  // `acted` is what keeps this from hiding a banner the parent has not seen. If
  // every hour had already gone before they looked, nothing is actionable from
  // the start, and that case must still be shown — with the header × as its only
  // control, because no row has one.
  if (openCount === 0 && acted) return null;

  return (
    <section className="released" aria-label="Hours released when your cart expired">
      <div className="released-head">
        <span className="released-title">Your cart has expired</span>
        <button
          type="button"
          className="drop"
          aria-label="Dismiss the hours that were released"
          onClick={() => {
            for (const group of groups) rememberDismissed(group.key);
            setDismissed(groups.map((item) => item.key));
            setActed(true);
          }}
        >
          ×
        </button>
      </div>

      {visible.map((group) => (
        <ReleasedRow
          key={group.key}
          group={group}
          productName={productNames.get(group.capacityTierId)}
          onStatus={report}
          onAdded={added}
          onDrop={drop}
        />
      ))}
    </section>
  );
}

/**
 * One row, which asks for itself whether it can still be had.
 *
 * Per row rather than all at once, and only on the way in: re-checking the lot
 * after every action makes every row blink for nothing.
 *
 * A 404 is the only answer that means the hour is unavailable — `useSlot` keeps
 * `gone` separate from `failed` for exactly this reason. So a row whose check
 * FAILED still offers Add: if the hour has really gone, the Add will say so and
 * the row flips then, which is honest, where "Taken" on a failed request would be
 * a statement about the world made on the strength of a broken request.
 */
function ReleasedRow({
  group,
  productName,
  onStatus,
  onAdded,
  onDrop,
}: {
  group: Group;
  productName: string | undefined;
  onStatus: (key: string, status: RowStatus) => void;
  onAdded: (key: string) => void;
  onDrop: (key: string) => void;
}) {
  const getToken = useAccessToken();
  const { state } = useSlot(group.publishedSlotId);
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);

  const taken = gone || state.status === 'gone';
  const status: RowStatus = taken ? 'taken' : state.status === 'loading' ? 'checking' : 'open';

  useEffect(() => {
    onStatus(group.key, status);
  }, [group.key, status, onStatus]);

  async function add() {
    if (busy) return;
    setBusy(true);
    try {
      // Sequentially, and with the same product and the same children it was
      // released with. There is no live cart at this point, so the first line
      // starts a fresh ten minutes.
      //
      // If only some children fit — a group that has filled up since — the ones
      // that went in are held, this row flips to Taken, and the next read brings
      // the row back carrying only the child who missed out. Which is what
      // happened, stated plainly.
      for (const athlete of group.athletes) {
        await addCartLine(getToken, {
          publishedSlotId: group.publishedSlotId,
          athleteClientId: athlete.id,
          serviceId: group.serviceId,
          capacityTierId: group.capacityTierId,
        });
      }
      onAdded(group.key);
    } catch {
      // No message: the row changing to Taken says it, and a sentence under a
      // row saying the same thing twice is §4's defect.
      setGone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={taken ? 'released-line taken' : 'released-line'}>
      <span className="released-line-text">
        <span className="name">{when(group.slotStart)}</span>
        {productName ? <span className="detail">{productName}</span> : null}
        {group.athletes.map((athlete) => (
          <span key={athlete.id} className="detail">
            {athlete.name}
          </span>
        ))}
      </span>

      {taken ? (
        <span className="released-taken">Taken</span>
      ) : (
        <>
          <button type="button" className="released-add" onClick={add} disabled={busy} aria-busy={busy}>
            <BusyLabel busy={busy} working="Adding…">
              Add
            </BusyLabel>
          </button>
          <button
            type="button"
            className="drop"
            aria-label={`Dismiss ${when(group.slotStart)}`}
            onClick={() => onDrop(group.key)}
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Released lines grouped into the hours they came from.
 *
 * The key is the hour AND the product: the same hour released as two different
 * products is two different things a parent could put back, and merging them
 * would put one Add button on a choice nobody made.
 */
function byHour(lines: ReleasedLine[]): Group[] {
  const groups = new Map<string, Group>();

  for (const line of lines) {
    const key = `${line.published_slot_id}:${line.capacity_tier_id}`;
    const existing = groups.get(key);

    if (existing) {
      existing.athletes.push({ id: line.athlete_client_id, name: line.athlete_name });
      continue;
    }

    groups.set(key, {
      key,
      publishedSlotId: line.published_slot_id,
      serviceId: line.service_id,
      capacityTierId: line.capacity_tier_id,
      slotStart: line.slot_start,
      athletes: [{ id: line.athlete_client_id, name: line.athlete_name }],
    });
  }

  return [...groups.values()];
}
